import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { RegisterOrgInput } from "@ndis/contracts";
import { api, setToken } from "../lib/api";
import AuthShell from "../components/AuthShell";

const STATES = ["NSW", "VIC", "QLD", "SA", "WA", "TAS", "NT", "ACT"] as const;

const field = "input";

export default function Register() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    orgName: "", abn: "", ownerName: "", email: "", password: "", state: "NSW",
  });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm({ ...form, [k]: e.target.value });

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const parsed = RegisterOrgInput.safeParse(form);
    if (!parsed.success) {
      const issue = parsed.error.issues[0];
      setError(`${issue?.path.join(".")}: ${issue?.message}`);
      return;
    }
    setBusy(true);
    try {
      const { token } = await api<{ token: string }>("/v1/auth/register", {
        method: "POST",
        body: JSON.stringify(parsed.data),
      });
      setToken(token);
      navigate("/");
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthShell>
      <form onSubmit={submit} className="w-full max-w-md card p-8 space-y-4">
        <div>
          <h1 className="text-xl font-semibold text-ink">Register your organisation</h1>
          <p className="text-sm text-mut">Start managing participants, rosters, and claims.</p>
        </div>
        <label className="block">
          <span className="label">Organisation name</span>
          <input value={form.orgName} onChange={set("orgName")} className={field} />
        </label>
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="label">ABN</span>
            <input value={form.abn} onChange={set("abn")} placeholder="11 digits" inputMode="numeric" className={field} />
          </label>
          <label className="block">
            <span className="label">State</span>
            <select value={form.state} onChange={set("state")} className={field}>
              {STATES.map((s) => <option key={s}>{s}</option>)}
            </select>
          </label>
        </div>
        <label className="block">
          <span className="label">Your name</span>
          <input value={form.ownerName} onChange={set("ownerName")} autoComplete="name" className={field} />
        </label>
        <label className="block">
          <span className="label">Email</span>
          <input type="email" value={form.email} onChange={set("email")} autoComplete="email" className={field} />
        </label>
        <label className="block">
          <span className="label">Password</span>
          <input type="password" value={form.password} onChange={set("password")} autoComplete="new-password" className={field} />
          <span className="text-xs text-faint">At least 10 characters</span>
        </label>
        {error && <p className="text-sm text-red-600">{error}</p>}
        <button disabled={busy}
          className="btn-primary w-full">
          {busy ? "Creating…" : "Create organisation"}
        </button>
        <p className="text-sm text-mut text-center">
          Already registered? <Link to="/login" className="text-teal-700 hover:underline">Sign in</Link>
        </p>
      </form>
    </AuthShell>
  );
}
